import Modal from "@/components/controls/Modal";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import CreatePartnerModal from "@/pages/partners/components/CreatePartnerModal";
import CreatePartnerContactModal from "@/pages/partners/contacts/components/CreatePartnerContactModal";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { UserData } from "@/entities/users/types";
import { LeadData, LeadType } from "@/pages/leads/types";
import {
  useCreateLeadMutation,
  useCreateLeadWithDreamerMutation,
  useLeadInboundCampaignTypes,
  useLeadOutboundCampaignTypes
} from "../hooks";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/utils/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import DreamerModeTabs, {
  DREAMER_MODE,
  MANUAL_MODE,
  DreamerMode
} from "@/components/controls/DreamerModeTabs";
import CreateLeadWithDreamer, {
  DreamerStep,
  LeadDreamerDetails
} from "./CreateLeadWithDreamer";
import { emailRegex } from "@/utils/validation";
import { isAxiosError } from "axios";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { excludePartnerFields } from "@/entities/dataTables/helpers";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject,
  getFieldNameByDisplayType
} from "@/utils/entityFieldsUtils/formFields";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import useBoundStore from "@/services/store/BoundStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { getCampaignTypeOptions } from "./LeadDetailFields/utils";
import { getUsersOptions } from "@/entities/forms/helpers";
import UserAvatar from "@/components/controls/UserAvatar";
import { useProducts } from "@/entities/settings/hooks";
import {
  useCheckCompanyDuplicates,
  useCompanyByName,
  useResetCompaniesInfinite
} from "@/pages/companies/hooks";
import { extractDomain, isValidWebsite } from "@/utils/domains";
import {
  usePersonByEmail,
  useResetPeopleInfinite
} from "@/entities/people/hooks";
import { CandidateData } from "@/hooks/useOpenOnNew";

export const OTHER_COMPANY_OPTION_VALUE = "c-other";
export const OTHER_PERSON_OPTION_VALUE = "p-other";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: UserData;
  resetPage?: () => void;
  /** Standalone Leads page only — embedded callers need the synchronous `onSuccess(id)` the async Dreamer path can't give. */
  allowDreamer?: boolean;
  onSuccess?: (newLeadId: string) => void;
  companyId?: string;
  personId?: string;
  partnerId?: string;
  candidateData?: CandidateData | null;
  onClose?: () => void;
}

const CreateLeadModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  userData,
  resetPage,
  allowDreamer = false,
  onSuccess,
  companyId,
  personId,
  partnerId,
  candidateData,
  onClose
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const dreamerFormRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const createLead = useCreateLeadMutation();
  const createWithDreamer = useCreateLeadWithDreamerMutation();

  // `enabled` resolves async — gate the render on `loading` or the flag-OFF branch paints first and remounts the form.
  const { enabled: dreamerFlagEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlags.CRM_8515_CREATE_LEAD_WITH_DREAMER
  );
  // allowDreamer gates both, so embedded call sites get neither the tab nor the spinner.
  const dreamerEnabled = allowDreamer && dreamerFlagEnabled;
  const dreamerFlagLoading = allowDreamer && flagLoading;
  const [activeTab, setActiveTab] = useState<DreamerMode>(DREAMER_MODE);
  const [dreamerEmail, setDreamerEmail] = useState("");
  const [showDreamerValidation, setShowDreamerValidation] = useState(false);
  const [dreamerStep, setDreamerStep] = useState<DreamerStep>("email");
  const [dreamerDetailsValid, setDreamerDetailsValid] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const isDreamerTab = dreamerEnabled && activeTab === DREAMER_MODE;

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<LeadType | null>(null);
  // A chosen partner makes both company and the primary contact optional;
  // chosen partner contacts also make the primary contact optional.
  const [hasPartner, setHasPartner] = useState(false);
  const [hasPartnerContacts, setHasPartnerContacts] = useState(false);
  const [selectedReferringCompanyId, setSelectedReferringCompanyId] =
    useState<string>("");
  const [isOtherReferringCompanySelected, setIsOtherReferringCompanySelected] =
    useState<boolean>(false);
  const [isOtherReferringPersonSelected, setIsOtherReferringPersonSelected] =
    useState<boolean>(false);
  const [view, setView] = useState<
    "companies" | "people" | "leads" | "partners" | "partnerContacts"
  >("leads");
  const [referringView, setReferringView] = useState<
    "referringCompanies" | "referringPeople" | null
  >(null);

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);

  const users = useBoundStore((state) => state.users.entities);
  const inboundCampaignTypes =
    useLeadInboundCampaignTypes().data?.inboundCampaignTypes ?? [];
  const outboundCampaignTypes =
    useLeadOutboundCampaignTypes().data?.outboundCampaignTypes ?? [];
  const products = useProducts().data?.products ?? [];

  const hasCandidateEmail = Boolean(candidateData?.email);
  const hasCandidateCompany = Boolean(candidateData?.companyName);
  const [candidateCascadeDone, setCandidateCascadeDone] = useState(false);
  // The form setter the pre-fill last ran against; a remounted form brings a new one.
  const prefilledFormSetterRef = useRef<unknown>(null);
  // The company and person chosen so far, created ones included, for a form remounted after the cascade.
  const selectedIdsRef = useRef({ companyId: "", personId: "" });
  const [pendingPersonCreation, setPendingPersonCreation] = useState(false);

  const personLookup = usePersonByEmail(
    candidateData?.email ?? "",
    hasCandidateEmail
  );
  const matchedPerson = personLookup.data?.people?.[0] ?? null;
  const personSearchDone =
    hasCandidateEmail && !personLookup.isLoading && !personLookup.isFetching;

  const companyLookup = useCompanyByName(
    candidateData?.companyName ?? "",
    hasCandidateCompany
  );
  const matchedCompany = companyLookup.data?.companies?.[0] ?? null;
  const companySearchDone =
    hasCandidateCompany &&
    !companyLookup.isLoading &&
    !companyLookup.isFetching;

  // The enrichment-failure link carries only the email; its domain finds the company the failed attempt resolved.
  // isValidWebsite takes the email as is: it reads the part after "@" and rejects personal-mail and malformed domains.
  const candidateEmail = candidateData?.email ?? "";
  const candidateDomain =
    !hasCandidateCompany && isValidWebsite(candidateEmail)
      ? (extractDomain(candidateEmail) ?? "")
      : "";
  const hasCandidateDomain = candidateDomain.length > 0;
  const companyDomainCheck = useCheckCompanyDuplicates(
    candidateDomain,
    hasCandidateDomain
  );
  // Exact domain match only, as the With Dreamer tab decides "Already in CRM".
  const domainMatchedCompanyId =
    companyDomainCheck.data?.exactMatches?.[0]?.companyId ?? null;
  // Settled, not "not loading": a paused lookup must not let the one-shot cascade run without it.
  const companyDomainSearchDone =
    hasCandidateDomain &&
    (companyDomainCheck.isSuccess || companyDomainCheck.isError);

  const {
    data: entityFieldsResponse,
    isSuccess: isFieldsSuccess,
    isError: isFieldsError
  } = useEntityFields(EntityType.LEAD);
  // FormNew reads defaults once at mount, so it mounts once the fields request succeeds or fails.
  // After a failure the form has no inputs, so a refetch remounting it loses nothing and brings the SDR owner default.
  // Products need no gate here: the productId effect below sets the default whenever they arrive.
  const isManualFormReady = isFieldsSuccess || isFieldsError;
  const updateEntityField = useUpdateEntityField(EntityType.LEAD);
  const enablePartnerships = useEnablePartnerships();
  const visibleFields = useMemo(
    () =>
      enablePartnerships
        ? entityFieldsResponse?.fields
        : excludePartnerFields(entityFieldsResponse?.fields ?? []),
    [entityFieldsResponse?.fields, enablePartnerships]
  );

  // Reset hooks to refetch options after creating new entities
  const resetCompaniesInfinite = useResetCompaniesInfinite();
  const resetPeopleInfinite = useResetPeopleInfinite();

  const {
    options: referringCompanyOptions,
    onSearch: onReferringCompanySearch,
    isLoading: isLoadingReferringCompanies,
    searchTerm: referringCompanySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    alwaysEnabled: selectedSource === LeadType.REFERRAL
  });

  const {
    options: referringPersonOptions,
    onSearch: onReferringPersonSearch,
    isLoading: isLoadingReferringPeople,
    searchTerm: referringPersonSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PERSON,
    filters: { companyId: selectedReferringCompanyId }
  });

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "leads",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      if (formSetValue) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          let currentValues = [];
          if (formGetValues) {
            const currentFormValues = formGetValues();
            currentValues =
              currentFormValues[currentFieldForNewOption.fieldKey] || [];
          }
          const newValues = Array.isArray(currentValues)
            ? [...currentValues, optionName]
            : [optionName];
          formSetValue(currentFieldForNewOption.fieldKey, newValues);
        } else {
          formSetValue(currentFieldForNewOption.fieldKey, optionName);
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const getInboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(inboundCampaignTypes);
  const getOutboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(outboundCampaignTypes);

  const getConditionalFields = useMemo((): FormFieldNewType[] => {
    const conditionalFields: FormFieldNewType[] = [];

    if (selectedSource === LeadType.INBOUND) {
      conditionalFields.push({
        name: "inboundCampaignType",
        label: t("leads.createModal.inboundCampaignType"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        fieldActionProps: {
          fieldName: "inboundCampaignType",
          options: getInboundCampaignTypeOptions()
        }
      });
    }

    if (selectedSource === LeadType.OUTBOUND) {
      conditionalFields.push({
        name: "outboundCampaignType",
        label: t("leads.createModal.outboundCampaignType"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        fieldActionProps: {
          fieldName: "outboundCampaignType",
          options: getOutboundCampaignTypeOptions()
        }
      });
    }

    if (selectedSource === LeadType.REFERRAL) {
      const otherOption = {
        id: OTHER_COMPANY_OPTION_VALUE,
        displayName: t("other")
      };

      conditionalFields.push({
        name: "referringCompany",
        label: t("leads.createModal.referringCompany"),
        type: "singleSelect" as const,
        required: false,
        isDependentRequired: true,
        labelPosition: "left" as const,
        layout: isOtherReferringCompanySelected ? ("half" as const) : undefined,
        fieldActionProps: {
          fieldName: "referringCompany",
          options: referringCompanyOptions,
          showSearch: true,
          initialSearchTerm: referringCompanySearchTerm,
          onSearch: onReferringCompanySearch,
          isLoading: isLoadingReferringCompanies,
          fixedOptions: [otherOption],
          showFixedOptions: true,
          popoverTriggerClassName: "!h-9",
          entity: EntityType.COMPANY,
          popOverFooter: (
            <AddButtonFooter
              buttonText={t("leads.createModal.addNewCompany")}
              onClick={() => setReferringView("referringCompanies")}
            />
          ),
          onSelection: (value: any) => {
            const isOther = value === OTHER_COMPANY_OPTION_VALUE;
            setIsOtherReferringCompanySelected(isOther);
            setSelectedReferringCompanyId(isOther ? "" : (value as string));
            if (typeof formSetValue === "function") {
              formSetValue("referringPerson", "", {
                shouldValidate: true
              });
              if (!isOther) {
                formSetValue("otherCompanyName", "", {
                  shouldValidate: true
                });
                setIsOtherReferringPersonSelected(false);
                formSetValue("otherPersonName", "", {
                  shouldValidate: true
                });
              }
            }
          }
        }
      });

      if (isOtherReferringCompanySelected) {
        conditionalFields.push({
          name: "otherCompanyName",
          label: "",
          type: "text" as const,
          required: false,
          isDependentRequired: true,
          labelPosition: "left" as const,
          layout: "half" as const,
          fieldActionProps: {
            fieldName: "otherCompanyName",
            placeholder: t("leads.createModal.referringCompanyName")
          }
        });
      }
    }

    const shouldShowReferringPerson =
      (!isEmpty(selectedReferringCompanyId) &&
        !isOtherReferringCompanySelected) ||
      isOtherReferringCompanySelected;

    if (shouldShowReferringPerson) {
      const otherPersonOption = {
        id: OTHER_PERSON_OPTION_VALUE,
        displayName: t("other")
      };

      conditionalFields.push({
        name: "referringPerson",
        label: t("leads.createModal.referringPerson"),
        type: "singleSelect" as const,
        labelPosition: "left" as const,
        required: false,
        layout: isOtherReferringPersonSelected ? ("half" as const) : undefined,
        fieldActionProps: {
          fieldName: "referringPerson",
          options: referringPersonOptions,
          showSearch: true,
          initialSearchTerm: referringPersonSearchTerm,
          onSearch: onReferringPersonSearch,
          isLoading: isLoadingReferringPeople,
          popoverTriggerClassName: "!h-9",
          showFixedOptions: true,
          fixedOptions: [otherPersonOption],
          entity: EntityType.PERSON,
          popOverFooter: (
            <AddButtonFooter
              buttonText={t("leads.createModal.addNewPerson")}
              onClick={() => setReferringView("referringPeople")}
            />
          ),
          onSelection: (value: any) => {
            const isOther = value === OTHER_PERSON_OPTION_VALUE;
            setIsOtherReferringPersonSelected(isOther);
            if (!isOther && typeof formSetValue === "function") {
              formSetValue("otherPersonName", "", {
                shouldValidate: true
              });
            }
          }
        }
      });

      if (isOtherReferringPersonSelected) {
        conditionalFields.push({
          name: "otherPersonName",
          label: "",
          type: "text" as const,
          required: false,
          isDependentRequired: true,
          labelPosition: "left" as const,
          layout: "half" as const,
          fieldActionProps: {
            fieldName: "otherPersonName",
            placeholder: t("leads.createModal.referringPersonName")
          }
        });
      }
    }

    return conditionalFields;
  }, [
    selectedSource,
    selectedReferringCompanyId,
    isOtherReferringCompanySelected,
    isOtherReferringPersonSelected,
    referringView,
    referringCompanyOptions,
    referringPersonOptions,
    referringCompanySearchTerm,
    referringPersonSearchTerm,
    isLoadingReferringCompanies,
    isLoadingReferringPeople,
    t
  ]);

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    hasUserEnteredDealName: false,
    setView: (view) => {
      if (
        view === "companies" ||
        view === "people" ||
        view === "partners" ||
        view === "partnerContacts"
      ) {
        setView(view);
      }
    },
    entityType: EntityType.LEAD,
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    fieldResetKeys: {},
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    visibleFields,
    t,
    formFieldsContext
  );

  const partnerFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNERS
  );
  const partnerContactsFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNER_CONTACTS
  );

  const formSections: FormSectionProps[] = useMemo(() => {
    const sections: FormSectionProps[] = baseSections.map((section) => ({
      ...section,
      fields: section.fields?.map((field) => {
        let enhancedField = {
          ...field,
          labelPosition: "left" as const,
          layout: "flex" as const
        };
        if (field.name === "sdrOwner" || field.name === "aeOwner") {
          enhancedField.fieldActionProps = {
            fieldName: field.name,
            options: getUsersOptions(users),
            showSearch: true,
            renderIcon: (value: string | number) => (
              <UserAvatar userData={users[String(value)]} size="M" />
            )
          };
        }
        if (field.name === "sdrOwner") {
          enhancedField.defaultValue = userData.id;
        }
        if (field.name === "companyId" && companyId) {
          enhancedField.defaultValue = companyId;
        }
        if (field.name === "contactId" && personId) {
          enhancedField.defaultValue = personId;
        }
        if (field.name === "companyId" && hasPartner) {
          enhancedField.required = false;
        }
        if (field.name === "contactId" && (hasPartner || hasPartnerContacts)) {
          enhancedField.required = false;
        }
        if (field.name === "productId" && products?.length === 1) {
          enhancedField.defaultValue = products[0]?.id;
        }
        if (field.name === "source") {
          enhancedField.fieldActionProps = {
            ...(enhancedField.fieldActionProps as any),
            fieldName: "source",
            onSelection: (value: any) => {
              setSelectedSource(Number(value as string) as LeadType);
            }
          } as any;
        }

        return enhancedField;
      })
    }));

    if (sections.length > 0 && getConditionalFields.length > 0) {
      const conditionalFieldsWithLayout = getConditionalFields.map((field) => ({
        ...field,
        labelPosition: "left" as const,
        layout: field.layout || "flex"
      }));

      const fields = sections[0].fields || [];
      const sourceFieldIndex = fields.findIndex(
        (field) => field.name === "source"
      );

      if (sourceFieldIndex !== -1) {
        const newFields = [
          ...fields.slice(0, sourceFieldIndex + 1),
          ...conditionalFieldsWithLayout,
          ...fields.slice(sourceFieldIndex + 1)
        ];

        sections[0] = {
          ...sections[0],
          fields: newFields,
          halfFieldClassName:
            isOtherReferringCompanySelected || isOtherReferringPersonSelected
              ? "flex-[2]"
              : undefined
        };
      } else {
        sections[0] = {
          ...sections[0],
          fields: [...fields, ...conditionalFieldsWithLayout]
        };
      }
    }

    return sections;
  }, [
    baseSections,
    getConditionalFields,
    isOtherReferringCompanySelected,
    isOtherReferringPersonSelected,
    hasPartner,
    hasPartnerContacts
  ]);

  // Both tabs stay mounted, so closing checks both; distinct key so the manual form's own fields aren't clobbered.
  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => ({
      ...(formGetValues?.() || {}),
      ...(dreamerEmail.trim() ? { dreamerEmail } : {})
    }),
    formSections
  );

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setSelectedSource(null);
      setHasPartner(false);
      setHasPartnerContacts(false);
      setSelectedReferringCompanyId("");
      setIsOtherReferringCompanySelected(false);
      setIsOtherReferringPersonSelected(false);
      setView("leads");
      setReferringView(null);
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setIsFormValid(false);
      setCandidateCascadeDone(false);
      prefilledFormSetterRef.current = null;
      setPendingPersonCreation(false);
      setActiveTab(DREAMER_MODE);
      setDreamerEmail("");
      setShowDreamerValidation(false);
      setDreamerStep("email");
      setDreamerDetailsValid(false);
      setIsQueueing(false);
      onClose?.();
    }
  }, [isOpen]);

  // A refetch after a failure unmounts the form; drop its setter and validity so nothing writes to or submits it.
  useEffect(() => {
    if (isManualFormReady) return;
    setFormSetValue(null);
    setFormGetValues(null);
    setIsFormValid(false);
  }, [isManualFormReady]);

  useEffect(() => {
    selectedIdsRef.current = {
      companyId: selectedCompanyId,
      personId: selectedPersonId
    };
  }, [selectedCompanyId, selectedPersonId]);

  // Deep links only come from the enrichment-failure notification — retrying Dreamer would fail the same way.
  // Layout effect: the modal instance is persistent, so the flip must land before the open paints
  // or the empty Dreamer tab flashes for a frame.
  useLayoutEffect(() => {
    if (isOpen && candidateData) {
      setActiveTab(MANUAL_MODE);
    }
  }, [isOpen, candidateData]);

  // Stable backend codes; 403/404 are dead ends and must not read as transient (404 = backend flag off).
  const dreamerQueueErrorTitle = (error: unknown): string => {
    const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
    switch (detail) {
      case "user_not_provisioned":
        return t("leads.createModal.withDreamer.errorNotProvisioned");
      case "invalid_email":
        return t("leads.createModal.withDreamer.invalidEmail");
      case "not_found":
        return t("leads.createModal.withDreamer.errorNotAvailable");
      default:
        return t("leads.createModal.withDreamer.queueError");
    }
  };

  const handleDreamerContinue = () => {
    setShowDreamerValidation(true);
    if (!emailRegex.test(dreamerEmail.trim())) {
      return;
    }
    setDreamerStep("details");
  };

  const handleDreamerSubmit = async (details: LeadDreamerDetails) => {
    const trimmedEmail = dreamerEmail.trim();
    if (!emailRegex.test(trimmedEmail) || isQueueing) {
      return;
    }
    setIsQueueing(true);
    // mutateAsync rejects even with onError supplied — unguarded, the toast never renders.
    try {
      await createWithDreamer.mutateAsync({ email: trimmedEmail, ...details });
      toast.success(
        <ToastBody title={t("leads.createModal.withDreamer.queued")} />,
        { duration: 5000 }
      );
      setIsOpen(false);
    } catch (error) {
      setIsQueueing(false);
      toast.error(
        <ToastBody
          title={dreamerQueueErrorTitle(error)}
          actions={
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          }
        />,
        { icon: null }
      );
    }
  };

  useEffect(() => {
    if (companyId) {
      setSelectedCompanyId(companyId);
      if (typeof formSetValue === "function") {
        formSetValue("companyId", companyId, {
          shouldValidate: true
        });
      }
    }
    if (personId) {
      setSelectedPersonId(personId);
      if (typeof formSetValue === "function") {
        formSetValue("contactId", personId, {
          shouldValidate: true
        });
      }
    }
  }, [companyId, personId, formSetValue]);

  useEffect(() => {
    if (partnerId && partnerFieldName && typeof formSetValue === "function") {
      formSetValue(partnerFieldName, partnerId, { shouldValidate: true });
    }
  }, [partnerId, partnerFieldName, formSetValue]);

  // Ensure defaults that depend on async data are applied after form mounts
  useEffect(() => {
    if (typeof formSetValue !== "function") return;
    if (products?.length === 1) {
      formSetValue("productId", products[0]?.id, { shouldValidate: true });
    }
  }, [formSetValue, products]);

  // Pre-fill lead name and auto-select matched company/person: until the cascade settles, then once per new form
  useEffect(() => {
    if (!candidateData || !isOpen) return;
    if (typeof formSetValue !== "function") return;
    if (
      candidateCascadeDone &&
      prefilledFormSetterRef.current === formSetValue
    ) {
      return;
    }
    prefilledFormSetterRef.current = formSetValue;

    const personParts: string[] = [];
    if (candidateData.firstName) personParts.push(candidateData.firstName);
    if (candidateData.lastName) personParts.push(candidateData.lastName);
    const nameParts: string[] = [];
    if (personParts.length > 0) nameParts.push(personParts.join(" "));
    if (candidateData.companyName) nameParts.push(candidateData.companyName);
    const leadName = nameParts.join(" - ");
    if (leadName) {
      formSetValue("name", leadName, { shouldValidate: true });
    }

    // A form remounted after the cascade keeps what was chosen or created for it, not just the lookup match.
    // Without a company name, a matched person's own company outranks the domain match, so the pair never contradicts itself.
    const companyToSelect =
      (candidateCascadeDone && selectedIdsRef.current.companyId) ||
      matchedCompany?.id ||
      (!hasCandidateCompany && matchedPerson?.companyId) ||
      domainMatchedCompanyId;
    const personToSelect =
      (candidateCascadeDone && selectedIdsRef.current.personId) ||
      matchedPerson?.id;
    if (companyToSelect) {
      setSelectedCompanyId(companyToSelect);
      formSetValue("companyId", companyToSelect, { shouldValidate: true });
    }
    if (personToSelect) {
      setSelectedPersonId(personToSelect);
      formSetValue("contactId", personToSelect, { shouldValidate: true });
    }
  }, [
    candidateData,
    isOpen,
    formSetValue,
    matchedCompany,
    domainMatchedCompanyId,
    matchedPerson,
    hasCandidateCompany,
    candidateCascadeDone
  ]);

  // Cascade: once both searches resolve, trigger creation flows as needed
  // Order: company first (if missing), then person (if missing)
  const hasCandidatePerson = Boolean(
    candidateData?.firstName || candidateData?.lastName || candidateData?.email
  );

  useEffect(() => {
    if (!candidateData || !isOpen || candidateCascadeDone) return;
    // Wait for the mounted form: marking the cascade done first would skip the one-shot pre-fill above.
    if (!isManualFormReady || typeof formSetValue !== "function") return;
    // Nothing to search for -- just pre-fill the lead name
    if (!hasCandidateCompany && !hasCandidateEmail) return;

    // Wait for the domain match too, so a person created next is linked to that company.
    const companyResolved = hasCandidateCompany
      ? companySearchDone
      : hasCandidateDomain
        ? companyDomainSearchDone
        : true;
    const personResolved = hasCandidateEmail ? personSearchDone : true;
    if (!companyResolved || !personResolved) return;

    setCandidateCascadeDone(true);

    if (hasCandidateCompany && !matchedCompany) {
      setView("companies");
    } else if (hasCandidatePerson && !matchedPerson) {
      setView("people");
    }
  }, [
    candidateData,
    isOpen,
    companySearchDone,
    companyDomainSearchDone,
    personSearchDone,
    matchedCompany,
    matchedPerson,
    hasCandidateCompany,
    hasCandidateDomain,
    hasCandidateEmail,
    hasCandidatePerson,
    candidateCascadeDone,
    formSetValue,
    isManualFormReady
  ]);

  // After company creation, cascade to person creation if person is still missing
  useEffect(() => {
    if (pendingPersonCreation && selectedCompanyId) {
      setPendingPersonCreation(false);
      setView("people");
    }
  }, [pendingPersonCreation, selectedCompanyId]);

  useEffect(() => {
    if (typeof formSetValue === "function" && selectedSource !== null) {
      formSetValue("inboundCampaignType", "", { shouldValidate: true });
      formSetValue("outboundCampaignType", "", { shouldValidate: true });
      formSetValue("referringCompany", "", { shouldValidate: true });
      formSetValue("referringPerson", "", { shouldValidate: true });
      formSetValue("otherCompanyName", "", { shouldValidate: true });
      formSetValue("otherPersonName", "", { shouldValidate: true });

      setSelectedReferringCompanyId("");
      setIsOtherReferringCompanySelected(false);
      setIsOtherReferringPersonSelected(false);
    }
  }, [selectedSource, formSetValue]);

  const getHasConditionalFieldsBeenFilled = (
    watchAllFields: Record<string, any>
  ) => {
    if (selectedSource === LeadType.INBOUND) {
      return Boolean(watchAllFields?.inboundCampaignType);
    }
    if (selectedSource === LeadType.OUTBOUND) {
      return Boolean(watchAllFields?.outboundCampaignType);
    }
    if (selectedSource === LeadType.REFERRAL) {
      const hasReferringCompany = Boolean(watchAllFields?.referringCompany);
      if (isOtherReferringCompanySelected) {
        return hasReferringCompany && Boolean(watchAllFields?.otherCompanyName);
      }
      return hasReferringCompany;
    }
    return true;
  };

  const handleSubmit = async (
    data: LeadData & {
      otherCompanyName?: string;
      otherPersonName?: string;
    }
  ) => {
    setIsSubmitting(true);
    const referringCompanyValue = data.referringCompany;

    const referringPersonValue = data.referringPerson;

    const transformFormData: LeadData = {
      ...data,
      name: data.name?.trim(),
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      ...mapEmptyToNull({
        inboundCampaignType: data.inboundCampaignType,
        outboundCampaignType: data.outboundCampaignType,
        referringCompany: referringCompanyValue,
        otherCompanyName: isOtherReferringCompanySelected
          ? data.otherCompanyName?.trim()
          : null,
        referringPerson: referringPersonValue,
        otherPersonName: isOtherReferringPersonSelected
          ? data.otherPersonName?.trim()
          : null
      }),
      numberOfLicenses: data.numberOfLicenses
        ? Number(data.numberOfLicenses)
        : null,
      source: data.source
    };

    await createLead.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        await resetPage?.();
        await onSuccess?.(response.id);
        toast.success(
          <ToastBody
            title={`${data.name} ${t("leads.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/leads/${response.id}`);
                }}
                variant="primary"
              >
                {t("leads.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        setIsOpen(false);
      },
      onError: (_error: Error) => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("leads.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });
    }
    if (hasCandidatePerson && !matchedPerson) {
      setPendingPersonCreation(true);
    }
  };

  const onPersonCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedPersonId(id);
    if (typeof formSetValue === "function") {
      formSetValue("contactId", id, {
        shouldValidate: true
      });
    }
  };

  const onPartnerCreation = (id: string, name: string) => {
    if (!id || !name || !partnerFieldName) return;
    if (typeof formSetValue === "function") {
      formSetValue(partnerFieldName, id, { shouldValidate: true });
    }
  };

  const onPartnerContactCreation = (id: string, name: string) => {
    if (!id || !name || !partnerContactsFieldName) return;
    if (typeof formSetValue === "function") {
      formSetValue(partnerContactsFieldName, id, { shouldValidate: true });
    }
  };

  const onReferringCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedReferringCompanyId(id);
    setIsOtherReferringCompanySelected(false);
    if (typeof formSetValue === "function") {
      formSetValue("referringCompany", id, {
        shouldValidate: true
      });
      formSetValue("otherCompanyName", "", {
        shouldValidate: true
      });
      formSetValue("referringPerson", "", {
        shouldValidate: true
      });
    }
    resetCompaniesInfinite();
    setReferringView(null);
  };

  const onReferringPersonCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setIsOtherReferringPersonSelected(false);
    if (typeof formSetValue === "function") {
      formSetValue("referringPerson", id, {
        shouldValidate: true
      });
      formSetValue("otherPersonName", "", {
        shouldValidate: true
      });
    }
    resetPeopleInfinite();
    setReferringView(null);
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-lead-modal"
        contentContainerClassname="w-[640px]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t("leads.createModal.title")}
          </Text>
        }
        content={
          <div
            className={cn(
              "flex flex-col w-full p-6 overflow-y-auto",
              // g6 (24px) between the switch and the Manually form; the Dreamer tab keeps its own rhythm.
              isDreamerTab ? "gap-3" : "gap-6",
              isDreamerTab ? "max-h-[60vh]" : "h-[60vh]"
            )}
            data-test="create-lead-modal-content"
          >
            {/* Figma hides the mode switch once Continue expands the details step. */}
            {dreamerEnabled && !dreamerFlagLoading && dreamerStep === "email" ? (
              <DreamerModeTabs
                labels={{
                  withDreamer: t("dreamerCreate.withDreamerTab"),
                  manually: t("dreamerCreate.manuallyTab")
                }}
                value={activeTab}
                onChange={setActiveTab}
                dataTest="create-lead-modal-tabs"
              />
            ) : null}

            {dreamerFlagLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
            {isDreamerTab ? (
              <CreateLeadWithDreamer
                email={dreamerEmail}
                setEmail={setDreamerEmail}
                showValidation={showDreamerValidation}
                step={dreamerStep}
                userData={userData}
                formRef={dreamerFormRef}
                onDetailsValidityChange={setDreamerDetailsValid}
                onSubmitDetails={handleDreamerSubmit}
              />
            ) : null}

            {/* Hidden, not unmounted: remounting react-hook-form wipes typed values on a tab peek. */}
            {/* Content gap is 12px; design wants g6 (24px) between the switch and the first field. */}
            <div className={cn(isDreamerTab && "hidden")}>
              {isManualFormReady ? (
                <FormNew
                  sections={formSections}
                  isFormValid={(
                    isValid: boolean,
                    watchAllFields: Record<string, any>
                  ) => {
                    const hasConditionalFieldsBeenFilled =
                      getHasConditionalFieldsBeenFilled(watchAllFields);
                    setIsFormValid(isValid && hasConditionalFieldsBeenFilled);
                    const partnerChosen = !!(
                      partnerFieldName && watchAllFields?.[partnerFieldName]
                    );
                    const partnerContactsChosen = !!(
                      partnerContactsFieldName &&
                      watchAllFields?.[partnerContactsFieldName]
                    );
                    setHasPartner((prev) =>
                      prev === partnerChosen ? prev : partnerChosen
                    );
                    setHasPartnerContacts((prev) =>
                      prev === partnerContactsChosen ? prev : partnerContactsChosen
                    );
                  }}
                  onSubmit={handleSubmit}
                  formRef={formRef}
                  onSetValue={handleSetValue}
                  shouldNotResetOnSubmit={true}
                  data-test="create-lead-modal-form"
                />
              ) : (
                <div
                  className="flex h-32 items-center justify-center"
                  data-test="create-lead-modal-form-loading"
                >
                  <Spinner />
                </div>
              )}
            </div>
              </>
            )}
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-lead-modal-footer"
          >
            <Button
              data-test="create-lead-modal-cancel-button"
              variant="secondary"
              disabled={isQueueing}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            {/* Footer mirrors the content's loading gate — no manual submit while the flag decides the tab. */}
            {dreamerFlagLoading ? null : isDreamerTab ? (
              dreamerStep === "email" ? (
                <Button
                  data-test="create-lead-modal-dreamer-continue-button"
                  disabled={dreamerEmail.trim().length === 0}
                  onClick={handleDreamerContinue}
                >
                  {t("leads.createModal.withDreamer.continue")}
                </Button>
              ) : (
                <Button
                  data-test="create-lead-modal-dreamer-submit-button"
                  disabled={!dreamerDetailsValid || isQueueing}
                  onClick={() => dreamerFormRef.current?.requestSubmit()}
                >
                  {t("leads.createModal.withDreamer.submit")}
                </Button>
              )
            ) : (
              <Button
                data-test="create-lead-modal-submit-button"
                disabled={!isFormValid || isSubmitting}
                onClick={handleCreateNew}
              >
                {t("leads.createModal.createNew")}
              </Button>
            )}
          </div>
        }
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("leads");
          }
        }}
        onSuccess={onCompanyCreation}
        defaultName={candidateData?.companyName}
      />
      <CreatePersonModal
        isOpen={view === "people"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("leads");
          }
        }}
        companyId={selectedCompanyId}
        onSuccess={onPersonCreation}
        defaultFirstName={candidateData?.firstName}
        defaultLastName={candidateData?.lastName}
        defaultEmail={candidateData?.email}
      />
      <CreatePartnerModal
        isOpen={view === "partners"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("leads");
          }
        }}
        onSuccess={onPartnerCreation}
      />
      <CreatePartnerContactModal
        isOpen={view === "partnerContacts"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("leads");
          }
        }}
        onSuccess={onPartnerContactCreation}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
      <CreateCompanyModal
        isOpen={referringView === "referringCompanies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setReferringView(null);
          }
        }}
        onSuccess={onReferringCompanyCreation}
      />
      <CreatePersonModal
        isOpen={referringView === "referringPeople"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setReferringView(null);
          }
        }}
        companyId={selectedReferringCompanyId}
        onSuccess={onReferringPersonCreation}
      />
      <UnsavedChangesWarningDialog {...warningDialogProps} />
    </>
  );
};

export default CreateLeadModal;
